import React, { useEffect, useState } from 'react';

import { PublicLayoutHeader } from '@/components/PublicLayout/components/PublicLayoutHeader';
import { Loader } from '@/components/shared/Loader';
import { appStoreUrl, googlePlayUrl, inviteLinkUrl } from '@/config/urls';
import { Team } from '@/graphql';
import applyParams from '@/helpers/applyParams';
import { useFingerprint } from '@/hooks/useFingerprint';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import { useFireAnalyticsPageView } from '@/providers/AnalyticsProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { Capacitor } from '@capacitor/core';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { isMobile } from 'react-device-detect';
import { useParams } from 'react-router';
import { useCopyToClipboard } from 'react-use';

import styles from '../Login/LoginPage.module.scss';
import EmailForm from './components/EmailForm';
import { Layout } from './components/Layout';
import VerifyForm from './components/VerifyForm';

const isCapacitor = Capacitor.isNativePlatform();
const isIOS = Capacitor.getPlatform() === 'ios';
const isAndroid = Capacitor.getPlatform() === 'android';
const isNeither = !isIOS && !isAndroid;

const AppStoreButton = () => (
	<a href={appStoreUrl}>
		<img
			src="/assets/download-app-store.svg"
			className={styles['button']}
		/>
	</a>
);

const GooglePlayButton = () => (
	<a href={googlePlayUrl}>
		<img
			src="/assets/download-google-play.svg"
			className={styles['button']}
		/>
	</a>
);

interface InviteRes {
	team: Team;
	memberCount: number;
	adminDisplayName: string;
}

interface DeepLinkParams {
	width?: string;
	height?: string;
	platform?: string;
	language?: string;
	destinationUrl?: string;
}

const recordDeepLink = async (url: string, params: DeepLinkParams) => {
	// Check if any value is falsy. If it is, don't record a deeplink as
	// we can't be certain that the device will be the same later
	if (Object.values(params).map(Boolean).includes(false)) {
		return;
	}
	const res = await axios.post<InviteRes>(`${url}/deeplink`, params);
	return res.data;
};

const getInvite = async (url: string, token?: string) => {
	if (!token) {
		throw new Error('Token invalid');
	}
	const res = await axios.get<InviteRes>(`${url}/link/${token}`);
	return res.data;
};

const getTitle = (data: InviteRes) => {
	let base = `You've been invited to join ${data.team.name} with ${data.adminDisplayName}`;
	if (data.memberCount > 0) {
		base += ` and ${data.memberCount} other${
			data.memberCount > 1 ? 's' : ''
		}`;
	}
	return base;
};

const InviteLink = () => {
	const { token } = useParams();
	const {
		api: { url },
	} = useEnvironment();
	const [email, setEmail] = useState<string | null>(null);
	const fingerprint = useFingerprint();
	const { enqueueSnackbar } = useSnackbar();
	const [value, copy] = useCopyToClipboard();
	const [title] = useFormattedTitle('Asaph | Join team');

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const { isLoading, data, error } = useQuery<InviteRes>({
		queryKey: ['get-invite', token],
		queryFn: () => getInvite(url, token),
	});

	useEffect(() => {
		// Record a deeplink only if the user is on mobile web
		if (isMobile && !isCapacitor) {
			const params: DeepLinkParams = {
				...fingerprint,
				destinationUrl: token && applyParams(inviteLinkUrl, { token }),
			};
			recordDeepLink(url, params);
		}
	}, [url, token, fingerprint]);

	useEffect(() => {
		if (value.value) {
			enqueueSnackbar('Link copied', {
				anchorOrigin: { vertical: 'top', horizontal: 'center' },
			});
		}
	}, [value, enqueueSnackbar]);

	if (isLoading) {
		return <Loader />;
	}

	if (error || !data) {
		return (
			<Layout title={title}>
				<>
					<h1 className={styles.heading}>Expired Invite</h1>
					<p className={styles.text}>
						Sorry, this invite link has expired.
					</p>
				</>
			</Layout>
		);
	}

	// Show the download app view only if the user is on mobile web
	if (isMobile && !isCapacitor) {
		const appUrl = isAndroid
			? 'intent://app.asaph.io/#Intent;scheme=https;package=io.asaph;end'
			: 'asaph://app.asaph.io';
		return (
			<Layout
				title={title}
				logo={<img src="/assets/asaph-app-icon-1024.png" />}
				footer={
					<div style={{ paddingBottom: '20px', textAlign: 'center' }}>
						{/* Mobile web implementation */}
						{isNeither && (
							<>
								Want to use the web app? <br /> Open&nbsp;
								<b onClick={() => copy('https://app.asaph.io')}>
									app.asaph.io
								</b>
								&nbsp;in your favourite browser.
							</>
						)}
						{/* Mobile iOS and Android implementation */}
						{!isNeither && (
							<>
								Already have the app?&nbsp;
								<a
									href={appUrl}
									target="_blank"
									rel="noreferrer"
								>
									<b>Open Asaph</b>
								</a>
							</>
						)}
					</div>
				}
				header={
					data && (
						<PublicLayoutHeader>
							{getTitle(data)}
						</PublicLayoutHeader>
					)
				}
			>
				<>
					<h1 className={styles['heading-full']}>
						Start by downloading the Asaph mobile app
					</h1>
					<div className={styles['download-app']}>
						{isIOS || isNeither ? <AppStoreButton /> : null}
						{isAndroid || isNeither ? <GooglePlayButton /> : null}
					</div>
				</>
			</Layout>
		);
	}

	// Otherwise, the user is on desktop web or in mobile app
	return (
		<Layout
			title={title}
			header={
				data && (
					<PublicLayoutHeader>{getTitle(data)}</PublicLayoutHeader>
				)
			}
		>
			<>
				{!email && <EmailForm onChange={setEmail} />}
				{!!email && <VerifyForm email={email} onChange={setEmail} />}
			</>
		</Layout>
	);
};

export default InviteLink;
